import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { categoryTotalsFromSeries } from 'src/helpers/signupBreakdown';
import { SignupBreakdownChartSeries } from 'src/interfaces/SignupBreakdown';

ChartJS.register(ArcElement, Legend, Tooltip);

interface Props {
  series: SignupBreakdownChartSeries;
  height?: number;
}

const SLICE_COLORS = (palette: Theme['palette']) => [
  palette.primary.main,
  palette.secondary.main,
  palette.success.main,
  palette.warning.main,
  palette.info.main,
  palette.error.main,
];

const SignupBreakdownPie = ({ series, height = 280 }: Props) => {
  const theme = useTheme();
  const colors = useMemo(() => SLICE_COLORS(theme.palette), [theme.palette]);

  const data = useMemo(() => {
    const totals = categoryTotalsFromSeries(series);
    return {
      labels: series.labels,
      datasets: [
        {
          data: totals,
          backgroundColor: series.labels.map((_, index) => colors[index % colors.length]),
        },
      ],
    };
  }, [colors, series]);

  const axisColor = theme.palette.text.secondary;

  return (
    <Box sx={{ height, width: '100%' }}>
      <Doughnut
        data={data}
        options={{
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: {
              display: series.labels.length > 1,
              position: 'bottom',
              labels: { color: axisColor },
            },
            tooltip: {
              callbacks: {
                label: context => {
                  const label = context.label ?? '';
                  const value = context.parsed ?? 0;
                  return `${label}: ${value}`;
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default SignupBreakdownPie;
